/**
 * Tooltip Context — provides tooltip content for all portal components.
 *
 * Fetches DB-sourced tooltips on mount and caches them for the session.
 * Falls back to static defaults when the backend is unreachable.
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { authFetch } from '../api/authFetch';
import { useAuth } from './AuthContext';

interface TooltipData {
  title: string;
  description: string;
  portal: string;
}

interface TooltipContextType {
  /** Get tooltip by component key. Returns undefined if not found. */
  getTooltip: (key: string) => TooltipData | undefined;
  /** All loaded tooltips keyed by component_key */
  tooltips: Record<string, TooltipData>;
  /** Whether tooltips have been loaded */
  loaded: boolean;
}

const TooltipContext = createContext<TooltipContextType>({
  getTooltip: () => undefined,
  tooltips: {},
  loaded: false,
});

/**
 * Static fallback tooltips used when the backend is unreachable.
 */
const STATIC_TOOLTIPS: Record<string, TooltipData> = {
  'navbar.logo': { title: 'KPMG', description: 'KPMG Discovery IQ Agent', portal: 'shared' },
  'navbar.title': { title: 'Portal Title', description: 'Shows the current portal and your specialization.', portal: 'shared' },
  'navbar.notifications': { title: 'Notifications', description: 'Real-time alerts for assessment updates and report deliveries.', portal: 'shared' },
  'navbar.org_chip': { title: 'Organisation', description: 'Your registered organization from Microsoft Entra ID sign-in.', portal: 'shared' },
  'navbar.user_menu': { title: 'Account Menu', description: 'View your profile, switch portals, or sign out.', portal: 'shared' },
};

export const TooltipProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tooltips, setTooltips] = useState<Record<string, TooltipData>>(STATIC_TOOLTIPS);
  const [loaded, setLoaded] = useState(false);
  const { isAuthenticated } = useAuth();
  const fetched = useRef(false);

  useEffect(() => {
    // Only fetch when the user is authenticated; retry once after login
    if (!isAuthenticated || fetched.current) return;

    let cancelled = false;
    authFetch('/api/tooltips')
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.success && data.tooltips) {
          setTooltips((prev) => ({ ...prev, ...data.tooltips }));
          fetched.current = true;
        }
      })
      .catch(() => {
        // Use static fallbacks — tooltips are best-effort
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const getTooltip = useCallback(
    (key: string): TooltipData | undefined => tooltips[key],
    [tooltips],
  );

  return (
    <TooltipContext.Provider value={{ getTooltip, tooltips, loaded }}>
      {children}
    </TooltipContext.Provider>
  );
};

export const useTooltips = () => useContext(TooltipContext);

export default TooltipContext;
